import React from 'react';
import { Box, Modal, Typography } from '@drone-js/core';
import { useSelector } from 'react-redux';
import { selectConfigFromBootstrap, selectFeatureBehaviorFromBootstrap } from 'store/slice/bootstrap';
import { body } from 'core-framework/window/document';
import { StringFormatter } from '@agoda/react-handyman';
import TextToggler from 'component/common/TextToggler/TextToggler';
import Portal from 'component/common/Portal/Portal';
import AgodaMarkdown from 'component/common/AgodaMarkdown/AgodaMarkdown';
import ContentContainer from 'component/common/ContentContainer/ContentContainer';
import { trackElementEvent } from 'core/messaging/analytics/analytics';
import { CityContent } from 'core/type/BootstrapConfig.types';
import { ActivitiesCityHeaderDescriptionShowMoreClickEventActivitiesFieldsV1 } from '@agoda/analytics-messages-definition-activities/events.split';
import { ActivitiesCityHeaderDescriptionShowMoreClickEventActivitiesManifestV1 } from '@agoda/analytics-messages-definition-activities/manifest/events';
import { selectScreenSize } from 'store/slice/screen';
import { ExperimentId } from 'core/type/ExperimentId';
import { useExperimentIsB } from 'component/common/Experiment/Experiment';
import { ActivityImage } from 'component/common/ActivityImage/ActivityImage';

const defaultCityContent: CityContent = {
    description: '',
    imageUrls: {
        desktop: 'https://cdn6.agoda.net/cdn-activities/images/1/city-page-header-default-background.jpg',
        mobile: 'https://cdn6.agoda.net/cdn-activities/images/1/city-page-header-default-background-mspa.jpg',
    },
};

const getImageUrl = (cityContent: CityContent, isMobileScreen: boolean) => {
    const imageUrl = isMobileScreen ? cityContent.imageUrls.mobile : cityContent.imageUrls.desktop;
    if (imageUrl) return imageUrl;
    return isMobileScreen ? defaultCityContent.imageUrls.mobile : defaultCityContent.imageUrls.desktop;
};

interface ContentProps {
    screenSize: { mobile: boolean; desktop: boolean };
    cityName: string;
    cityContent: CityContent;
    cms: Record<number, string>;
    modalVisible: boolean;
    setModalVisible: () => void;
    setModalInvisible: () => void;
    isPageContainerSpacing: boolean;
    isRemoveCityPageImage: boolean;
}

const Content: React.FC<ContentProps> = ({
    screenSize,
    cityName,
    cityContent,
    cms,
    modalVisible,
    setModalVisible,
    setModalInvisible,
    isPageContainerSpacing,
    isRemoveCityPageImage,
}) => (
    <ContentContainer enforceDefaultSpacing={true} data-testid="activities-city-header-content" height="100%">
        <Box
            height="100%"
            display="flex"
            flexDirection="column"
            pt={isRemoveCityPageImage ? '32' : undefined}
            py={isRemoveCityPageImage ? undefined : screenSize.mobile || isPageContainerSpacing ? '24' : '32'}
            justifyContent="flex-end"
            width={isRemoveCityPageImage && screenSize.mobile ? undefined : '960px'}
        >
            <Typography
                tag="h3"
                variant={screenSize.mobile ? 'body-s-strong' : 'heading-default'}
                aria-level={2}
                textColor={isRemoveCityPageImage ? 'base.default' : 'base.inverted'}
            >
                {cms[298696]}
            </Typography>
            <Typography tag="h1" variant="heading-3xl" aria-level={2} textColor={isRemoveCityPageImage ? 'base.default' : 'base.inverted'}>
                {cityName}
            </Typography>
            {screenSize.desktop && cityContent.description.length > 0 && (
                <>
                    <TextToggler
                        elementName="activities-city-header-description"
                        text={cityContent.description}
                        defaultExpanded={false}
                        threshold={225}
                        textVariant="body-default"
                        textColor={isRemoveCityPageImage ? 'base.subtle' : 'base.inverted.subtle'}
                        onToggleOverride={setModalVisible}
                        toggleButtonElementName="activities-description-toggle-button"
                        isTextLinkInheritColor={true}
                    />
                    <Portal container={body}>
                        <Modal
                            data-testid="activities-city-header-description-modal"
                            title={StringFormatter.format(cms[52986], '', cityName)}
                            visible={modalVisible}
                            onClose={setModalInvisible}
                        >
                            <Typography tag="p" variant="body-default" aria-level={2} textColor="base.default">
                                <AgodaMarkdown input={cityContent.description} />
                            </Typography>
                        </Modal>
                    </Portal>
                </>
            )}
        </Box>
    </ContentContainer>
);

Content.displayName = 'Content';

const CityHeader: React.FC = () => {
    const [modalVisible, setModalVisibleValue] = React.useState(false);
    const { geo, cms } = useSelector(selectConfigFromBootstrap);
    const isPageContainerSpacing = useSelector(selectFeatureBehaviorFromBootstrap).PageContainerSpacing;
    const screenSize = useSelector(selectScreenSize);
    const cityName = geo?.cityNameTranslated || geo?.cityName || '';
    const cityContent = geo?.cityContent ?? defaultCityContent;

    const setModalVisible = () => {
        setModalVisibleValue(true);
        trackElementEvent<ActivitiesCityHeaderDescriptionShowMoreClickEventActivitiesFieldsV1>(ActivitiesCityHeaderDescriptionShowMoreClickEventActivitiesManifestV1.ids, {}, {});
    };
    const setModalInvisible = () => setModalVisibleValue(false);

    const imageUrl = getImageUrl(cityContent, screenSize.mobile);

    const increaseHeroImageGradient = useExperimentIsB(ExperimentId.AWL_581);
    const RemoveCityPageImage = useExperimentIsB(ExperimentId.ACT_5130);

    return (
        <>
            {RemoveCityPageImage ? (
                <Box backgroundColor="base.surface" data-testid="activities-city-header">
                    <Content
                        screenSize={screenSize}
                        cityName={cityName}
                        cityContent={cityContent}
                        cms={cms}
                        modalVisible={modalVisible}
                        setModalVisible={setModalVisible}
                        setModalInvisible={setModalInvisible}
                        isPageContainerSpacing={isPageContainerSpacing}
                        isRemoveCityPageImage={true}
                    />
                </Box>
            ) : (
                <Box data-testid="activities-city-header" position="relative" overflow="hidden" height={screenSize.desktop ? '420px' : '240px'}>
                    <ActivityImage
                        data-testid="activities-city-header-image"
                        alt={cityName}
                        src={imageUrl}
                        objectFit="cover"
                        responsive="original"
                        loading="lazy-blur"
                        height="100%"
                        width="100%"
                    />
                    {increaseHeroImageGradient ? (
                        <Box
                            data-testid="activities-city-header-extra-gradient"
                            width="100%"
                            height="100%"
                            position="absolute"
                            bottom={0}
                            backgroundColor={{
                                angle: 180,
                                stops: [
                                    {
                                        backgroundColor: 'base.transparent',
                                        position: '20%',
                                    },
                                    {
                                        backgroundColor: 'base.overlay.strong',
                                        position: '100%',
                                    },
                                ],
                            }}
                        >
                            <Box
                                width="100%"
                                height="100%"
                                backgroundColor={{
                                    angle: -130,
                                    stops: [
                                        {
                                            backgroundColor: 'base.transparent',
                                            position: '20%',
                                        },
                                        {
                                            backgroundColor: 'base.overlay.strong',
                                            position: '100%',
                                        },
                                    ],
                                }}
                            >
                                <Content
                                    screenSize={screenSize}
                                    cityName={cityName}
                                    cityContent={cityContent}
                                    cms={cms}
                                    modalVisible={modalVisible}
                                    setModalVisible={setModalVisible}
                                    setModalInvisible={setModalInvisible}
                                    isPageContainerSpacing={isPageContainerSpacing}
                                    isRemoveCityPageImage={false}
                                />
                            </Box>
                        </Box>
                    ) : (
                        <Box
                            data-testid="activities-city-header-gradient"
                            width="100%"
                            height="100%"
                            position="absolute"
                            bottom={0}
                            backgroundColor={{
                                stops: [
                                    {
                                        backgroundColor: 'base.transparent',
                                        position: '20%',
                                    },
                                    {
                                        backgroundColor: 'base.overlay.strong',
                                        position: '100%',
                                    },
                                ],
                            }}
                        >
                            <Content
                                screenSize={screenSize}
                                cityName={cityName}
                                cityContent={cityContent}
                                cms={cms}
                                modalVisible={modalVisible}
                                setModalVisible={setModalVisible}
                                setModalInvisible={setModalInvisible}
                                isPageContainerSpacing={isPageContainerSpacing}
                                isRemoveCityPageImage={false}
                            />
                        </Box>
                    )}
                </Box>
            )}
        </>
    );
};

CityHeader.displayName = 'CityHeader';

export default CityHeader;
